import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  User,
  Phone,
  MapPin,
  Briefcase,
  Clock,
  Globe2,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  ClockAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import type { BudgetCompatibility, EvaluationGroupFreelancer } from "@/types/evaluation";
import type { EvaluationFormItem, EvaluationResponse } from "@/types/evaluation-form";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/lib/sanitize-html";

interface EvaluationFreelancerCardProps {
  freelancer: EvaluationGroupFreelancer;
  formId?: number;
  onEvaluate: (freelancer: EvaluationGroupFreelancer) => void;
  canRemove?: boolean;
  onRemove?: (freelancer: EvaluationGroupFreelancer) => void;
  onSendToReview?: (freelancer: EvaluationGroupFreelancer) => void;
  onBackToPending?: (freelancer: EvaluationGroupFreelancer) => void;
}

export function EvaluationFreelancerCard({
  freelancer,
  formId,
  onEvaluate,
  canRemove = false,
  onRemove,
  onSendToReview,
  onBackToPending,
}: EvaluationFreelancerCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Key dedicada de opções (id+nome), isolada do cache de shape completo em
  // ["agencias"] (last-writer-wins por key no TanStack).
  const { data: agenciasNomes } = useQuery({
    queryKey: ["agencias", "options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select("id, nome")
        .order("nome");
      if (error) throw error;
      return data;
    },
    enabled: !!freelancer.agencias_afins?.length,
  });

  // Fetch form items
  const { data: formItems = [], isLoading: isLoadingItems } = useQuery({
    queryKey: ["evaluation_form_items", formId],
    queryFn: async () => {
      if (!formId) return [];
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("evaluation_form_items")
        .select("*")
        .eq("form_id", formId)
        .order("display_order");

      if (error) throw error;
      return data as EvaluationFormItem[];
    },
    enabled: !!formId && freelancer.is_evaluated,
  });

  // Fetch evaluation responses for this freelancer
  const { data: evaluationResponses = [], isLoading: isLoadingResponses } = useQuery({
    queryKey: ["evaluation_responses", freelancer.id],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("evaluation_responses")
        .select("*")
        .eq("evaluation_group_freelancer_id", freelancer.id);

      if (error) throw error;
      return data as EvaluationResponse[];
    },
    enabled: freelancer.is_evaluated,
  });

  const getAgenciaNome = (id: string) => {
    return agenciasNomes?.find((a) => a.id === id)?.nome || id;
  };


  const getScoreColor = (score: number | null) => {
    if (!score) return "bg-gray-200";
    if (score >= 8) return "bg-green-500";
    if (score >= 6) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getScoreIndicatorClass = (score: number | null) => {
    const colorClass = getScoreColor(score);
    const map: Record<string, string> = {
      "bg-green-500": "[&>div]:bg-green-500",
      "bg-yellow-500": "[&>div]:bg-yellow-500",
      "bg-red-500": "[&>div]:bg-red-500",
      "bg-gray-200": "[&>div]:bg-gray-200",
    };
    return map[colorClass] || "[&>div]:bg-primary";
  };

  // Get the response value for a form item
  const getResponseValue = (itemId: number): number | null => {
    const response = evaluationResponses.find(r => r.form_item_id === itemId);
    return response?.value ?? null;
  };

  const getBudgetCompatibilityLabel = (value: BudgetCompatibility) => {
    const labels: Record<BudgetCompatibility, string> = {
      menos: "Abaixo da média",
      neutro: "Na média",
      mais: "Acima da média",
    };
    return labels[value] || "";
  };

  const isLoadingEvaluationData = isLoadingItems || isLoadingResponses;

  // Card para freelancer "Em Revisão"
  if (freelancer.is_in_review) {
    return (
      <Card className="overflow-hidden border-l-4 border-l-yellow-500 shadow-xs hover:shadow-md transition-shadow">
        <CardHeader className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 border-2 border-background">
                <AvatarImage src={undefined} />
                <AvatarFallback>
                  <User className="h-6 w-6 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg leading-none">
                    {freelancer.freelancer_name}
                  </h3>
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-300">
                    Em Revisão
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                  {freelancer.telefone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span>{freelancer.telefone}</span>
                    </div>
                  )}
                  {freelancer.cidade && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>{freelancer.cidade}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3 text-sm pt-2">
                <ExternalLinkButton
                  kind="portfolio"
                  url={freelancer.portfolio_url}
                  variant="inline"
                  hideWhenEmpty={false}
                  label={<span className="hidden sm:inline">Portfólio</span>}
                  icon={Globe2}
                  iconClassName="h-4 w-4"
                />

                <FreelancerLinkedInButton
                  freelancerId={freelancer.freelancer_id}
                  url={freelancer.linkedin_url}
                  variant="inline"
                  hideWhenEmpty={false}
                  label={<span className="hidden sm:inline">LinkedIn</span>}
                  iconClassName="h-4 w-4"
                />
              </div>
              <div className="flex items-center gap-2">
                {canRemove && onRemove && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemove(freelancer)}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                {onBackToPending && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onBackToPending(freelancer)}
                  >
                    Enviar para Pendentes
                  </Button>
                )}
                <Button onClick={() => onEvaluate(freelancer)} size="sm">
                  Avaliar
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-4">
          {freelancer.in_review_reason && (
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md text-sm">
              <span className="font-medium text-yellow-800">Motivo da revisão: </span>
              <span className="text-yellow-700">{freelancer.in_review_reason}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {freelancer.areas?.map((area) => (
              <Badge key={area} variant="secondary">
                {area}
              </Badge>
            ))}
            {freelancer.idiomas?.map((idioma) => (
              <Badge key={idioma} variant="outline">
                {idioma}
              </Badge>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            {freelancer.pretensao_salarial && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="p-1.5 bg-green-100 rounded-full">
                  <span className="font-bold text-green-700 text-xs">$</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs">Pretensão Salarial</span>
                  <span className="font-medium text-foreground">
                    {freelancer.pretensao_salarial}
                  </span>
                </div>
              </div>
            )}
            {freelancer.tempo_experiencia && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="p-1.5 bg-blue-100 rounded-full">
                  <Clock className="h-3 w-3 text-blue-700" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs">Experiência</span>
                  <span className="font-medium text-foreground">
                    {freelancer.tempo_experiencia}
                  </span>
                </div>
              </div>
            )}
            {freelancer.tipo_atuacao && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="p-1.5 bg-purple-100 rounded-full">
                  <Briefcase className="h-3 w-3 text-purple-700" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs">Atuação</span>
                  <span className="font-medium text-foreground">
                    {freelancer.tipo_atuacao}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Card para freelancer "Avaliado"
  if (freelancer.is_evaluated) {
    return (
      <Card className="overflow-hidden border-l-4 border-l-primary shadow-xs hover:shadow-md transition-shadow">
        <CardHeader className="p-4 pb-2">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 border-2 border-background">
                <AvatarImage src={undefined} />
                <AvatarFallback>
                  <User className="h-6 w-6 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg leading-tight">
                  {freelancer.freelancer_name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px] font-normal">
                    Avaliado em{" "}
                    {freelancer.evaluated_at &&
                      format(new Date(freelancer.evaluated_at), "dd/MM/yyyy", {
                        locale: ptBR,
                      })}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEvaluate(freelancer)}
                className="h-8 w-8 text-muted-foreground hover:text-primary"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-2 space-y-6">
          <div className="flex flex-wrap gap-4 items-center justify-between bg-muted/20 p-3 rounded-lg">
            {freelancer.nota_criterio_pessoal && (
              <div className="flex flex-col items-center">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Nota Pessoal
                </span>
                <div className="flex items-center gap-1">
                  <span className="text-2xl font-bold text-primary">
                    {freelancer.nota_criterio_pessoal}
                  </span>
                  <span className="text-sm text-muted-foreground">/10</span>
                </div>
              </div>
            )}
            
            {freelancer.compativel_grana && (
              <div className="flex flex-col items-center">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Orçamento
                </span>
                <Badge 
                  variant="default"
                  className="text-xs"
                >
                  {getBudgetCompatibilityLabel(freelancer.compativel_grana)}
                </Badge>
              </div>
            )}
          </div>

          {/* Dynamic form items based on evaluation responses */}
          {isLoadingEvaluationData ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-1.5">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-1.5 w-full" />
                </div>
              ))}
            </div>
          ) : formItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
              {formItems.map((item) => {
                const value = getResponseValue(item.id);
                return (
                  <div key={item.id} className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium text-muted-foreground">{item.label}</span>
                      <span className="font-bold">{value ?? "-"}</span>
                    </div>
                    <Progress 
                      value={value ? (value / item.max_value) * 100 : 0} 
                      className={cn("h-1.5", getScoreIndicatorClass(value))} 
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-2">
              Nenhum item de avaliação configurado
            </div>
          )}

          {freelancer.series_agencias && freelancer.series_agencias.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Porte de Agências
              </span>
              <div className="flex flex-wrap gap-2">
                {freelancer.series_agencias.map((porte) => (
                  <Badge key={porte} variant="secondary">
                    {porte}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {freelancer.outras_percepcoes && (
            <div className="bg-muted/30 p-3 rounded-md text-sm text-muted-foreground italic border-l-2 border-muted">
              "{freelancer.outras_percepcoes}"
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-background">
              <AvatarImage src={undefined} />
              <AvatarFallback>
                <User className="h-6 w-6 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg leading-none">
                  {freelancer.freelancer_name}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                {freelancer.telefone && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    <span>{freelancer.telefone}</span>
                  </div>
                )}
                {freelancer.cidade && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <span>{freelancer.cidade}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3 text-sm pt-2">
              <ExternalLinkButton
                kind="portfolio"
                url={freelancer.portfolio_url}
                variant="inline"
                hideWhenEmpty={false}
                label={<span className="hidden sm:inline">Portfólio</span>}
                icon={Globe2}
                iconClassName="h-4 w-4"
              />
              
              <FreelancerLinkedInButton
                freelancerId={freelancer.freelancer_id}
                url={freelancer.linkedin_url}
                variant="inline"
                hideWhenEmpty={false}
                label={<span className="hidden sm:inline">LinkedIn</span>}
                iconClassName="h-4 w-4"
              />
            </div>
            <div className="flex items-center gap-2">
              {canRemove && onRemove && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemove(freelancer)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              {onSendToReview && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSendToReview(freelancer)}
                  className="gap-1"
                >
                  <ClockAlert className="h-4 w-4" />
                  <span className="hidden sm:inline">Enviar para Revisão</span>
                </Button>
              )}
              <Button onClick={() => onEvaluate(freelancer)} size="sm">
                Avaliar
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-4">
        <div className="flex flex-wrap gap-2">
          {freelancer.areas?.map((area) => (
            <Badge key={area} variant="secondary">
              {area}
            </Badge>
          ))}
          {freelancer.idiomas?.map((idioma) => (
            <Badge key={idioma} variant="outline">
              {idioma}
            </Badge>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          {freelancer.pretensao_salarial && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="p-1.5 bg-green-100 rounded-full">
                <span className="font-bold text-green-700 text-xs">$</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs">Pretensão Salarial</span>
                <span className="font-medium text-foreground">
                  {freelancer.pretensao_salarial}
                </span>
              </div>
            </div>
          )}
          {freelancer.tempo_experiencia && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="p-1.5 bg-blue-100 rounded-full">
                <Clock className="h-3 w-3 text-blue-700" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs">Experiência</span>
                <span className="font-medium text-foreground">
                  {freelancer.tempo_experiencia}
                </span>
              </div>
            </div>
          )}
          {freelancer.tipo_atuacao && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="p-1.5 bg-purple-100 rounded-full">
                <Briefcase className="h-3 w-3 text-purple-700" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs">Atuação</span>
                <span className="font-medium text-foreground">
                  {freelancer.tipo_atuacao}
                </span>
              </div>
            </div>
          )}
        </div>

        {(freelancer.historia_profissional ||
          freelancer.experiencias_anteriores ||
          freelancer.objetivos) && (
          <div className="mt-4 border-t pt-4">
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-3 w-3 mr-1" /> Ocultar detalhes
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3 mr-1" /> Ver detalhes
                  profissionais
                </>
              )}
            </Button>

            {isExpanded && (
              <div className="mt-4 space-y-4 text-sm animate-in slide-in-from-top-2">
                {freelancer.historia_profissional && (
                  <div className="space-y-1">
                    <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                      História Profissional
                    </h4>
                    <div
                      className="text-muted-foreground prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(freelancer.historia_profissional),
                      }}
                    />
                  </div>
                )}
                {freelancer.experiencias_anteriores && (
                  <div className="space-y-1">
                    <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                      Experiências Anteriores
                    </h4>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {freelancer.experiencias_anteriores}
                    </p>
                  </div>
                )}
                {freelancer.objetivos && (
                  <div className="space-y-1">
                    <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                      Objetivos
                    </h4>
                    <div
                      className="text-muted-foreground prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(freelancer.objetivos),
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
